/*
 * Soft chime for countdown/alarm fire events. Uses Web Audio's
 * OscillatorNode + GainNode so we don't ship an audio file or depend
 * on an HTMLAudioElement that browsers might suspend in background
 * tabs.
 *
 * Three little sine-wave bells in quick succession (high → mid → low)
 * with a short attack-decay envelope. Pleasant in a classroom, not a
 * shrill kitchen-timer beep.
 *
 * Browser audio policy: Web Audio is gated until the page has had a
 * user gesture. We construct the AudioContext lazily inside playChime,
 * which is only called from a click-driven mutation (start countdown
 * or save alarm). That lets the very first fire play sound. For
 * alarms set today and triggered tomorrow morning with no prior
 * interaction, the takeover modal still opens — only the chime is
 * silent — and any subsequent click resumes audio.
 */

let ctx: globalThis.AudioContext | null = null;

function getContext(): globalThis.AudioContext | null {
  if (typeof window === 'undefined') return null;
  const Ctor =
    (window as unknown as { AudioContext?: typeof globalThis.AudioContext }).AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof globalThis.AudioContext })
      .webkitAudioContext;
  if (!Ctor) return null;
  if (!ctx) ctx = new Ctor();
  return ctx;
}

/** Resume the AudioContext if it was suspended (autoplay policy). */
export function primeAudio(): void {
  const c = getContext();
  if (c && c.state === 'suspended') void c.resume();
}

function playTone(
  c: globalThis.AudioContext,
  freq: number,
  startOffset: number,
  durationS: number,
  peakGain: number,
): void {
  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.type = 'sine';
  osc.frequency.value = freq;
  const now = c.currentTime + startOffset;
  // Short attack, exponential decay. Avoids the click of a hard cutoff.
  gain.gain.setValueAtTime(0, now);
  gain.gain.linearRampToValueAtTime(peakGain, now + 0.015);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + durationS);
  osc.connect(gain);
  gain.connect(c.destination);
  osc.start(now);
  osc.stop(now + durationS + 0.05);
}

export function playChime(): void {
  const c = getContext();
  if (!c) return;
  if (c.state === 'suspended') void c.resume();
  // E5 → C5 → G4 — descending major arpeggio, ~1.5s total.
  playTone(c, 659.25, 0.0, 0.55, 0.28);
  playTone(c, 523.25, 0.45, 0.6, 0.26);
  playTone(c, 392.0, 0.95, 0.85, 0.24);
}
